import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api/client";
import { useAuthStore } from "@/store/auth";
import { toast } from "sonner";
import {
  ChangePasswordFormData,
  LoginFormData,
  RequestPasswordResetFormData,
  ResetPasswordFormData,
  TwoFactorFormData,
} from "@/lib/validators/auth";
import { LoginResponse, AuthUser } from "../types";
import { queryClient } from "@/lib/api/query-client";
import { useAuditLog } from "@/hooks/use-audit-log";
import { AxiosError } from "axios";
import { teamMemberSchema } from "@/features/security/schemas/team";

export interface Pre2FALoginData {
  // user: AuthUser;
  message: string;
}

interface LoginResponseSchema {
  data: {
    info: LoginResponse;
  };
}

interface TwoFactorVerificationResponseSchema {
  data: {
    info: {
      data: AuthUser;
      accessToken: string;
      // expiresIn: number;
    };
  };
}

interface ProfileResponseSchema {
  data: {
    info: {
      admin: AuthUser;
    };
  };
}

interface Verify2FAPayload extends TwoFactorFormData {
  context: "login" | "setup";
}

export const useAuth = (on2faRequired: (data: Pre2FALoginData) => void) => {
  const navigate = useNavigate();
  const loginToStore = useAuthStore((state) => state.login);
  const { logAction } = useAuditLog();

  const loginMutation = useMutation<AuthUser, AxiosError, LoginFormData>({
    mutationFn: async (credentials) => {
      const { data } = await apiClient.post<LoginResponseSchema>(
        "/v1/admin/login",
        credentials,
      );
      return data.data.info.admin;
    },
    onSuccess: (admin, variables) => {
      // logAction("LOGIN_2FA_REQUIRED", `2FA required for ${variables.email}.`);
      on2faRequired({
        // user: admin,
        message: "Two-factor authentication required",
      });
    },
    onError: (error, variables) => {
      const errorMessage =
        (error.response?.data as { message?: string })?.message ||
        error.message;
      // logAction(
      //   "LOGIN_FAILURE",
      //   `Failed login attempt for ${variables.email}.`,
      //   { error: errorMessage }
      // );
      toast.error("Login Failed", {
        description:
          errorMessage || "Invalid email or password. Please try again.",
      });
      console.error("Login error:", error);
    },
  });

  return {
    login: loginMutation.mutate,
    isLoading: loginMutation.isPending,
  };
};

export const useProfile = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return useQuery<AuthUser, Error>({
    queryKey: ["profile"],
    queryFn: async () => {
      const response =
        await apiClient.get<ProfileResponseSchema>("/v1/admin/profile");

      // const rawAdmin = teamMemberSchema.parse(response.data.data.info.admin);
      const rawAdmin = response.data.data.info.admin;

      const effectivePermissions: string[] =
        rawAdmin.role.permissions?.map((p) => p.name) || [];

      const normalizedAdmin: AuthUser = {
        ...rawAdmin,
        permissions: effectivePermissions,
      };

      return normalizedAdmin;
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
  });
};

export const useLogout = () => {
  const { logAction } = useAuditLog();
  const logoutFromStore = useAuthStore((state) => state.logout);
  const navigate = useNavigate();

  return useMutation<void, AxiosError, void>({
    mutationFn: async () => {
      await apiClient.post("/v1/admin/logout");
    },
    onSuccess: () => {
      // logAction("LOGOUT_SUCCESS", `User logged out.`);
      logoutFromStore();
      toast.success("Signed Out", {
        description: "You have been successfully signed out.",
      });
      navigate("/login");
    },
    onError: (error) => {
      const errorMessage =
        (error.response?.data as { message?: string })?.message ||
        error.message;
      // logAction("LOGOUT_FAILURE", `Logout failed.`, { error: errorMessage });
      logoutFromStore();
      toast.error("Logout Failed", {
        description:
          "Could not sign out from the server, but you have been logged out locally.",
      });
      navigate("/login");
    },
  });
};

export const useChangePassword = () => {
  const { logAction } = useAuditLog();
  return useMutation<void, AxiosError, ChangePasswordFormData>({
    mutationFn: async (payload) => {
      await apiClient.post("/v1/admin/password/change", payload);
    },
    onSuccess: () => {
      // logAction("PASSWORD_CHANGE_SUCCESS", "User changed their password.");
      toast.success("Password Changed Successfully", {
        description:
          "Your password has been updated. Please use it for your next login.",
      });
    },
    onError: (error) => {
      const errorMessage =
        (error.response?.data as { message?: string })?.message ||
        error.message;
      // logAction("PASSWORD_CHANGE_FAILURE", "Failed to change password.", {
      //   error: errorMessage,
      // });
      toast.error("Password Change Failed", {
        description: errorMessage || "An unexpected error occurred.",
      });
    },
  });
};

export const useRequestPasswordReset = () => {
  const { logAction } = useAuditLog();
  return useMutation<void, AxiosError, RequestPasswordResetFormData>({
    mutationFn: async (payload) => {
      await apiClient.post("/v1/admin/password/reset-request", payload);
    },
    onSuccess: (_, variables) => {
      // logAction(
      //   "PASSWORD_RESET_REQUEST_SUCCESS",
      //   `Password reset requested for ${variables.email}.`
      // );
    },
    onError: (error, variables) => {
      const errorMessage =
        (error.response?.data as { message?: string })?.message ||
        error.message;
      // logAction(
      //   "PASSWORD_RESET_REQUEST_FAILURE",
      //   `Failed password reset request for ${variables.email}.`,
      //   { error: errorMessage }
      // );
      toast.error("Request Failed", {
        description: errorMessage || "Could not process your request.",
      });
    },
  });
};

export const useResetPassword = () => {
  const { logAction } = useAuditLog();
  const navigate = useNavigate();
  return useMutation<void, AxiosError, ResetPasswordFormData>({
    mutationFn: async (payload) => {
      await apiClient.post("/v1/admin/password/reset", payload);
    },
    onSuccess: (_, variables) => {
      // logAction(
      //   "PASSWORD_RESET_SUCCESS",
      //   `Password reset for ${variables.email}.`
      // );
      toast.success("Password Reset Successfully", {
        description: "You can now log in with your new password.",
      });
    },
    onError: (error, variables) => {
      const errorMessage =
        (error.response?.data as { message?: string })?.message ||
        error.message;
      toast.error("Reset Failed", {
        description:
          errorMessage || "The reset link may be invalid or expired.",
      });
    },
  });
};

export const useTwoFactorSetup = () => {
  const { logAction } = useAuditLog();
  return useMutation({
    mutationFn: async () => {
      // this endpoint has not been created by backend, but we include it here now because its necessary (to be modified)
      const { data } = await apiClient.post("/v1/admin/two-factor/setup");
      return data;
    },
    onSuccess: () => {
      // logAction("2FA_SETUP_INITIATED", "User initiated 2FA setup.");
      toast.info("2FA Setup Initiated", {
        description: "Scan the QR code with your authenticator app.",
      });
    },
    onError: (error) => {
      // logAction("2FA_SETUP_FAILURE", "Failed to initiate 2FA setup.", {
      //   error: error.message,
      // });
      toast.error("Setup Failed", {
        description: "Could not start the 2FA setup process.",
      });
    },
  });
};

export const useTwoFactorVerify = () => {
  const { logAction } = useAuditLog();
  const navigate = useNavigate();
  const loginToStore = useAuthStore((state) => state.login);

  return useMutation({
    mutationFn: async (payload: Verify2FAPayload) => {
      const { data } =
        await apiClient.post<TwoFactorVerificationResponseSchema>(
          "/v1/admin/two-factor/verify-otp",
          payload,
        );
      return data.data.info;
    },
    onSuccess: (data) => {
      if (data.data) {
        // const user = useAuthStore.getState().user;
        const user = {
          ...data.data,
          // permissions: ["*"], // harcoding permissions for now pending when backend adds permissions to the user object
        };
        if (user) {
          loginToStore(user);
          // logAction(
          //   "2FA_LOGIN_SUCCESS",
          //   `User ${user.email} completed 2FA login.`
          // );
          toast.success(`Welcome back, ${user.name}!`);
          navigate("/");
        }
      } else {
        // logAction("2FA_SETUP_SUCCESS", `User completed 2FA setup.`);
        toast.success("2FA Enabled Successfully!", {
          description:
            "Your account is now protected with Two-Factor Authentication.",
        });
        queryClient.invalidateQueries({ queryKey: ["profile"] });
      }
    },
    onError: (error: any) => {
      // logAction("2FA_VERIFY_FAILURE", "Failed to verify 2FA OTP.", {
      //   error: error.message,
      // });
      toast.error("Verification Failed", {
        description:
          error.response?.data?.message ||
          "The code is invalid or has expired.",
      });
    },
  });
};
